import React, { useEffect, useMemo, useState } from 'react'
import {
  FaCalendarAlt,
  FaCamera,
  FaEye,
  FaHeart,
  FaInfoCircle,
  FaPlus,
  FaSave,
  FaTrash,
  FaUsers
} from 'react-icons/fa'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Formik } from 'formik'
import { useQueryClient } from '@tanstack/react-query'

import api from 'services/api'

import { useItineraries } from 'contexts/itinerary.context'

import { TRAVEL_INTERESTS } from 'travelInterests'
import { getItineraryPublicPath } from 'utils/itineraryUrl'

import CollaborationPicker from 'components/collaborationPicker'

import DayCityEditor from './DayCityEditor'
import * as S from './style'

const DEFAULT_COVER = '/images/default_itinerary_cover.svg'

const isMadeItinerary = (made: unknown) =>
  made === true || String(made) === 'true'

const getLocalDateValue = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-')

const getYesterdayDate = () => {
  const date = new Date()
  date.setDate(date.getDate() - 1)
  return getLocalDateValue(date)
}

const addDays = (dateString: string, amount: number) => {
  if (!dateString) return null
  const date = new Date(`${dateString}T12:00:00`)
  date.setDate(date.getDate() + amount)
  return getLocalDateValue(date)
}

const buildDayPlan = (itinerary: any) => {
  if (Array.isArray(itinerary.dayPlan) && itinerary.dayPlan.length > 0) {
    return itinerary.dayPlan.map((day: any, index: number) => ({
      ...day,
      dayNumber: index + 1,
      date: day.date ? String(day.date).slice(0, 10) : null,
      title: day.title || '',
      cities: (day.cities || []).map((city: any) => ({
        ...city,
        images: city.images || [],
        description: city.description || ''
      }))
    }))
  }

  const dayCount = Math.max(Number(itinerary.days) || 1, 1)
  const cities = itinerary.cities || []

  return Array.from({ length: dayCount }, (_, index) => {
    const dayNumber = index + 1
    return {
      dayNumber,
      date:
        itinerary.made && itinerary.tripDate
          ? addDays(String(itinerary.tripDate).slice(0, 10), index)
          : null,
      title: '',
      cities: cities
        .filter(
          (city: any) => Number(city.dayNumber ?? city.order ?? 1) === dayNumber
        )
        .map((city: any) => ({
          ...city,
          images: city.images || [],
          description: city.description || ''
        }))
    }
  })
}

const emptyCity = () => ({
  name: '',
  description: '',
  latitude: 0,
  longitude: 0,
  images: [],
  accommodations: []
})

const UpdateItinerary = () => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const queryClient = useQueryClient()
  const { updateItinerary, uploadFile } = useItineraries()
  const [itinerary, setItinerary] = useState<any>(null)
  const [collaboration, setCollaboration] = useState<any>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    let active = true

    ;(async () => {
      try {
        setLoadError(null)

        // Permission is decided by the edit endpoint itself. Load it first so a
        // secondary collaboration request can never keep the page spinning.
        const itineraryResponse = await api.get(`/itineraries/id/${id}`, {
          timeout: 20000
        })

        if (!active) return

        const source = itineraryResponse.data?.itinerary
        if (!source?.id) {
          throw new Error('Roteiro indisponível para edição')
        }

        setItinerary({
          id: source.id,
          made: source.made,
          title: source.title,
          tripDate: source.tripDate
            ? String(source.tripDate).split('T')[0]
            : '',
          coverImage: source.coverImage || DEFAULT_COVER,
          summary: source.summary || '',
          days: source.days,
          spent: source.spent ?? 0,
          type: source.type,
          interests: (source.interests || [])
            .map(
              (item: any) =>
                item?.interest?.name ||
                item?.name ||
                (typeof item === 'string' ? item : '')
            )
            .filter(Boolean),
          collaboratorIds: [],
          cities: source.cities || [],
          dayPlan: buildDayPlan(source),
          inspiredFromItineraryId: source.inspiredFromItineraryId || null,
          inspirationSource: source.inspirationSource || null
        })

        try {
          const collaborationResponse = await api.get(
            `/itineraries/${id}/collaborators`
          )
          if (active) setCollaboration(collaborationResponse.data)
        } catch {
          if (active) setCollaboration(null)
        }
      } catch (error: any) {
        if (!active) return

        const status = Number(error?.response?.status)
        const timedOut =
          error?.code === 'ECONNABORTED' ||
          String(error?.message || '')
            .toLowerCase()
            .includes('timeout')
        const message =
          status === 403
            ? 'Você não tem permissão para editar este roteiro.'
            : status === 404
              ? 'Este roteiro não foi encontrado ou não está disponível para edição.'
              : timedOut
                ? 'O carregamento do roteiro demorou mais do que o esperado.'
                : 'Não foi possível abrir este roteiro para edição.'

        setLoadError(message)
        toast.error(message, { theme: 'dark' })
      }
    })()

    return () => {
      active = false
    }
  }, [history, id, loadAttempt])

  const refreshCollaboration = async () => {
    const response = await api.get(`/itineraries/${id}/collaborators`)
    setCollaboration(response.data)
  }

  const removeCollaborator = async (userId: string) => {
    await api.delete(`/itineraries/${id}/collaborators/${userId}`)
    await refreshCollaboration()
  }

  const leaveCollaboration = async () => {
    await api.delete(`/itineraries/${id}/collaborators/me`)
    history.push('/minha-conta')
  }

  const setUploadError = (key: string, message?: string) => {
    setUploadErrors((current) => {
      if (!message) {
        const next = { ...current }
        delete next[key]
        return next
      }

      return { ...current, [key]: message }
    })
  }

  const navItems = useMemo(
    () => [
      { id: 'informacoes', label: 'Informações', icon: <FaInfoCircle /> },
      { id: 'dia-a-dia', label: 'Dia a dia', icon: <FaCalendarAlt /> },
      { id: 'interesses', label: 'Interesses', icon: <FaHeart /> },
      { id: 'colaboracao', label: 'Colaboração', icon: <FaUsers /> }
    ],
    []
  )

  if (loadError) {
    return (
      <S.Loading>
        <S.LoadingCard role="alert">
          <FaInfoCircle />
          <strong>{loadError}</strong>
          <span>
            Seu roteiro não foi perdido. Você pode tentar carregar novamente ou
            voltar ao seu perfil.
          </span>
          <S.LoadingActions>
            <button
              type="button"
              className="primary"
              onClick={() => {
                setLoadError(null)
                setItinerary(null)
                setLoadAttempt((current) => current + 1)
              }}
            >
              Tentar novamente
            </button>
            <button type="button" onClick={() => history.push('/minha-conta')}>
              Voltar ao meu perfil
            </button>
          </S.LoadingActions>
        </S.LoadingCard>
      </S.Loading>
    )
  }

  if (!itinerary) {
    return (
      <S.Loading>
        <S.LoadingCard role="status" aria-live="polite" aria-busy="true">
          <S.LoadingSpinner />
          <strong>Carregando roteiro...</strong>
          <span>Preparando o editor e o dia a dia da sua viagem.</span>
        </S.LoadingCard>
      </S.Loading>
    )
  }

  return (
    <S.Page>
      {isSaving && (
        <S.SavingOverlay role="status" aria-live="polite" aria-busy="true">
          <S.SavingCard>
            <S.SavingSpinner />
            <strong>Salvando alterações...</strong>
            <span>
              Estamos atualizando dias, cidades, fotos e o mapa do roteiro.
            </span>
          </S.SavingCard>
        </S.SavingOverlay>
      )}
      <Formik
        initialValues={itinerary}
        enableReinitialize
        onSubmit={async (values: any) => {
          if (isSaving) return

          const activeUploadError = Object.values(uploadErrors)[0]
          if (activeUploadError) {
            toast.error(activeUploadError, { theme: 'dark' })
            return
          }

          const made = isMadeItinerary(values.made)

          if (made) {
            if (!values.tripDate) {
              toast.warning('Informe a data da viagem para um roteiro feito.', {
                theme: 'dark'
              })
              return
            }

            if (values.tripDate >= getLocalDateValue(new Date())) {
              toast.warning(
                'Para um roteiro feito, a data da viagem precisa estar no passado.',
                { theme: 'dark' }
              )
              return
            }
          }

          const invalidDayIndex = values.dayPlan.findIndex(
            (day: any) =>
              !(day.cities || []).some((city: any) =>
                String(city?.name || '').trim()
              )
          )

          if (invalidDayIndex >= 0) {
            toast.warning(
              `O Dia ${invalidDayIndex + 1} precisa ter pelo menos uma cidade cadastrada.`,
              { theme: 'dark' }
            )
            return
          }

          const normalizedDayPlan = values.dayPlan.map(
            (day: any, index: number) => ({
              ...day,
              dayNumber: index + 1,
              date: made ? day.date || addDays(values.tripDate, index) : null,
              cities: (day.cities || []).map(
                (city: any, cityIndex: number) => ({
                  ...city,
                  dayNumber: index + 1,
                  order: cityIndex + 1,
                  rate: null,
                  accommodations: city.accommodations || []
                })
              )
            })
          )

          setIsSaving(true)

          try {
            const success = await updateItinerary({
              ...values,
              made,
              tripDate: made ? values.tripDate : '',
              days: normalizedDayPlan.length,
              dayPlan: normalizedDayPlan
            })

            if (!success) {
              setIsSaving(false)
              return
            }

            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: ['Itineraries', id]
              }),
              queryClient.invalidateQueries({ queryKey: ['itineraries'] }),
              queryClient.invalidateQueries({
                queryKey: ['itinerariesUser']
              }),
              queryClient.invalidateQueries({
                queryKey: ['favoriteItineraries']
              }),
              queryClient.invalidateQueries({ queryKey: ['MapMarkers'] }),
              queryClient.invalidateQueries({
                queryKey: ['destination-catalog-summary']
              }),
              queryClient.invalidateQueries({
                queryKey: ['destination-catalog-state']
              }),
              queryClient.invalidateQueries({
                queryKey: ['AllItineraries']
              })
            ])

            history.push(getItineraryPublicPath({ id }))
          } catch {
            setIsSaving(false)
          }
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue
        }: any) => {
          const made = isMadeItinerary(values.made)

          const addDay = () => {
            const nextIndex = values.dayPlan.length
            setFieldValue('dayPlan', [
              ...values.dayPlan,
              {
                dayNumber: nextIndex + 1,
                date: made ? addDays(values.tripDate, nextIndex) : null,
                title: '',
                cities: [emptyCity()]
              }
            ])
          }

          return (
            <S.Form id="itinerary-update-form" onSubmit={handleSubmit}>
              <S.TopBar>
                <div>
                  <h1>Editar roteiro</h1>
                  <S.RouteMeta>
                    <span>
                      {values.title || 'Seu roteiro'} em {values.dayPlan.length}{' '}
                      {values.dayPlan.length === 1 ? 'dia' : 'dias'}
                    </span>
                    {collaboration?.collaborators?.some(
                      (item: any) => item.status === 'accepted'
                    ) && <S.CollabBadge>Roteiro colaborativo</S.CollabBadge>}
                  </S.RouteMeta>
                </div>
                <S.TopActions>
                  <S.SecondaryButton
                    type="button"
                    onClick={() => history.push(getItineraryPublicPath({ id }))}
                  >
                    <FaEye /> Visualizar roteiro
                  </S.SecondaryButton>
                  <S.PrimaryButton type="submit" disabled={isSaving}>
                    <FaSave /> {isSaving ? 'Salvando...' : 'Salvar alterações'}
                  </S.PrimaryButton>
                </S.TopActions>
              </S.TopBar>

              {itinerary?.inspirationSource && (
                <S.InspirationSourceNotice>
                  <FaInfoCircle />
                  <div>
                    <strong>
                      Inspirado em “{itinerary.inspirationSource.title}”
                    </strong>
                    <span>
                      Esta versão foi reescrita para você. Cidades, fatos e
                      ideias podem ser preservados, mas relatos pessoais do
                      autor não são copiados literalmente. A origem continua
                      vinculada ao roteiro-base.
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      history.push(
                        getItineraryPublicPath({
                          id: itinerary.inspirationSource.id
                        })
                      )
                    }
                  >
                    Ver roteiro-base
                  </button>
                </S.InspirationSourceNotice>
              )}

              <S.CoverHero $cover={values.coverImage || DEFAULT_COVER}>
                <S.CoverActions>
                  <label>
                    <FaCamera /> Trocar capa
                    <input
                      type="file"
                      accept="image/*,.heic"
                      onChange={async (event) => {
                        const file = event.target.files?.[0]
                        if (!file) return
                        setFieldValue(
                          'coverImage',
                          await uploadFile(file, 'itinerary_cover')
                        )
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setFieldValue('coverImage', DEFAULT_COVER)}
                  >
                    Usar capa padrão
                  </button>
                </S.CoverActions>
              </S.CoverHero>

              <S.Workspace>
                <S.Sidebar>
                  {navItems.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() =>
                        document.getElementById(item.id)?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        })
                      }
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </button>
                  ))}
                </S.Sidebar>

                <S.Main>
                  <S.Section id="informacoes">
                    <S.SectionHeader>
                      <div>
                        <h2>Informações</h2>
                        <p>Dados gerais do roteiro.</p>
                      </div>
                    </S.SectionHeader>

                    <S.InfoGrid>
                      <S.FieldGroup className="wide">
                        <label>Título da viagem</label>
                        <input
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </S.FieldGroup>

                      <S.FieldGroup>
                        <label>Tipo de roteiro</label>
                        <S.PillGroup>
                          <button
                            type="button"
                            className={made ? 'active' : ''}
                            onClick={() => setFieldValue('made', true)}
                          >
                            Feito
                          </button>
                          <button
                            type="button"
                            className={!made ? 'active' : ''}
                            onClick={() => {
                              setFieldValue('made', false)
                              setFieldValue('tripDate', '')
                              setFieldValue(
                                'dayPlan',
                                values.dayPlan.map((day: any) => ({
                                  ...day,
                                  date: null
                                }))
                              )
                            }}
                          >
                            Planejado
                          </button>
                        </S.PillGroup>
                      </S.FieldGroup>

                      {made && (
                        <S.FieldGroup>
                          <label>Data inicial</label>
                          <input
                            type="date"
                            name="tripDate"
                            max={getYesterdayDate()}
                            value={values.tripDate}
                            onChange={handleChange}
                          />
                        </S.FieldGroup>
                      )}

                      <S.FieldGroup>
                        <label>Custo total por pessoa</label>
                        <input
                          type="number"
                          name="spent"
                          min="0"
                          step="0.01"
                          value={values.spent}
                          onChange={handleChange}
                        />
                      </S.FieldGroup>

                      <S.FieldGroup className="full">
                        <label>Resumo da viagem</label>
                        <textarea
                          name="summary"
                          value={values.summary}
                          onChange={handleChange}
                          maxLength={5000}
                          placeholder="Conte, de forma breve, o que torna este roteiro especial."
                        />
                      </S.FieldGroup>
                    </S.InfoGrid>
                  </S.Section>

                  <S.Section id="dia-a-dia">
                    <S.SectionHeader>
                      <div>
                        <h2>Dia a dia da viagem</h2>
                        <p>
                          Organize cada dia da viagem. A primeira foto da
                          primeira cidade será usada como imagem representativa
                          do dia.
                        </p>
                      </div>
                      <S.AddDayButton type="button" onClick={addDay}>
                        <FaPlus /> Adicionar novo dia
                      </S.AddDayButton>
                    </S.SectionHeader>

                    <S.DaysList>
                      {values.dayPlan.map((day: any, dayIndex: number) => (
                        <S.DayCard key={day.id || dayIndex}>
                          <S.DayHeader>
                            <S.DayNumber>
                              Dia {dayIndex + 1}
                              {(day.cities || []).some((city: any) =>
                                String(city?.name || '').trim()
                              ) && (
                                <span>
                                  {(day.cities || [])
                                    .map((city: any) =>
                                      String(city?.name || '').trim()
                                    )
                                    .filter(Boolean)
                                    .join(' + ')}
                                </span>
                              )}
                            </S.DayNumber>

                            {made && (
                              <S.DayDate>
                                <FaCalendarAlt />
                                <input
                                  type="date"
                                  value={day.date || ''}
                                  onChange={(event) =>
                                    setFieldValue(
                                      `dayPlan.${dayIndex}.date`,
                                      event.target.value
                                    )
                                  }
                                />
                              </S.DayDate>
                            )}

                            <S.DayTitleInput
                              value={day.title || ''}
                              placeholder="Título do dia (opcional)"
                              onChange={(event) =>
                                setFieldValue(
                                  `dayPlan.${dayIndex}.title`,
                                  event.target.value
                                )
                              }
                            />

                            {values.dayPlan.length > 1 && (
                              <S.DeleteDayButton
                                type="button"
                                aria-label="Excluir dia"
                                onClick={() =>
                                  setFieldValue(
                                    'dayPlan',
                                    values.dayPlan.filter(
                                      (_: any, index: number) =>
                                        index !== dayIndex
                                    )
                                  )
                                }
                              >
                                <FaTrash />
                              </S.DeleteDayButton>
                            )}
                          </S.DayHeader>

                          <S.DayCities>
                            {(day.cities || []).map(
                              (city: any, cityIndex: number) => (
                                <DayCityEditor
                                  key={city.id || cityIndex}
                                  dayIndex={dayIndex}
                                  cityIndex={cityIndex}
                                  city={city}
                                  setFieldValue={setFieldValue}
                                  setUploadError={setUploadError}
                                  removeCity={() =>
                                    setFieldValue(
                                      `dayPlan.${dayIndex}.cities`,
                                      day.cities.filter(
                                        (_: any, index: number) =>
                                          index !== cityIndex
                                      )
                                    )
                                  }
                                />
                              )
                            )}
                          </S.DayCities>

                          <S.AddCityButton
                            type="button"
                            onClick={() =>
                              setFieldValue(`dayPlan.${dayIndex}.cities`, [
                                ...(day.cities || []),
                                emptyCity()
                              ])
                            }
                          >
                            <FaPlus /> Adicionar cidade ao dia
                          </S.AddCityButton>
                        </S.DayCard>
                      ))}
                    </S.DaysList>
                  </S.Section>

                  <S.Section id="interesses">
                    <S.SectionHeader>
                      <div>
                        <h2>Interesses</h2>
                        <p>Ajude outros viajantes a encontrar este roteiro.</p>
                      </div>
                    </S.SectionHeader>
                    <S.InterestGrid>
                      {TRAVEL_INTERESTS.map((interest) => {
                        const active = values.interests.includes(interest.value)
                        return (
                          <button
                            type="button"
                            key={interest.value}
                            className={active ? 'active' : ''}
                            onClick={() =>
                              setFieldValue(
                                'interests',
                                active
                                  ? values.interests.filter(
                                      (value: string) =>
                                        value !== interest.value
                                    )
                                  : [...values.interests, interest.value]
                              )
                            }
                          >
                            {interest.label}
                          </button>
                        )
                      })}
                    </S.InterestGrid>
                  </S.Section>

                  <S.Section id="colaboracao">
                    <S.SectionHeader>
                      <div>
                        <h2>Colaboração</h2>
                        <p>
                          {collaboration?.isOwner
                            ? 'Convide e gerencie quem participa deste roteiro.'
                            : 'Você participa deste roteiro como colaborador.'}
                        </p>
                      </div>
                      {collaboration?.isCollaborator &&
                        !collaboration?.isOwner && (
                          <S.SecondaryButton
                            type="button"
                            onClick={leaveCollaboration}
                          >
                            Sair do roteiro
                          </S.SecondaryButton>
                        )}
                    </S.SectionHeader>

                    {collaboration?.collaborators?.length > 0 && (
                      <S.Collaborators>
                        {collaboration.collaborators.map((item: any) => (
                          <S.Collaborator key={item.userId}>
                            <img
                              src={
                                item.profilePicture ||
                                '/images/profile-empty.png'
                              }
                              alt=""
                            />
                            <div>
                              <strong>
                                {item.firstname} {item.lastname}
                              </strong>
                              <span>
                                {item.status === 'accepted'
                                  ? 'Colaborador'
                                  : item.status === 'pending'
                                    ? 'Convite pendente'
                                    : 'Convite recusado'}
                              </span>
                            </div>
                            {collaboration?.isOwner && (
                              <button
                                type="button"
                                onClick={() => removeCollaborator(item.userId)}
                              >
                                Remover
                              </button>
                            )}
                          </S.Collaborator>
                        ))}
                      </S.Collaborators>
                    )}

                    {collaboration?.isOwner && (
                      <CollaborationPicker
                        selectedIds={values.collaboratorIds || []}
                        excludeIds={
                          collaboration.collaborators
                            ?.filter((item: any) => item.status !== 'rejected')
                            .map((item: any) => item.userId) || []
                        }
                        onChange={(ids) =>
                          setFieldValue('collaboratorIds', ids)
                        }
                      />
                    )}
                  </S.Section>
                </S.Main>
              </S.Workspace>
              <S.StickySaveBar>
                <div>
                  <strong>Editar roteiro</strong>
                  <span>Salve para aplicar as alterações no roteiro.</span>
                </div>
                <S.StickyActions>
                  <S.SecondaryButton
                    type="button"
                    onClick={() => history.push(getItineraryPublicPath({ id }))}
                  >
                    <FaEye /> Visualizar roteiro
                  </S.SecondaryButton>
                  <S.PrimaryButton type="submit" disabled={isSaving}>
                    <FaSave /> {isSaving ? 'Salvando...' : 'Salvar alterações'}
                  </S.PrimaryButton>
                </S.StickyActions>
              </S.StickySaveBar>
            </S.Form>
          )
        }}
      </Formik>
    </S.Page>
  )
}

export default UpdateItinerary
